import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiClientError, createPost, createToolbarActions, defaultToolbarLabels, getCaptcha, renderMarkdown } from '@suangua/shared';
import { useAuth } from '../../lib/auth/AuthContext';
import { BOARD_LABEL } from '../ui/atoms';

export interface EditorModalProps {
  open: boolean;
  /** 初始板块（可在弹层内切换） */
  board: 'chat' | 'divination' | 'qa';
  onClose: () => void;
  onPosted: (id: number) => void;
}

/** Markdown 工具栏动作表（shared 动作表，与回复编辑器同源） */
const TOOLBAR_ACTIONS = createToolbarActions(defaultToolbarLabels);

const CONTENT_MAX = 50000;

/**
 * 发帖弹窗（原型发帖编辑器复刻，玄墨鎏金 modal 风格）
 * - 板块分段切换 + 占卜贴灵验标记（未定/灵/不灵/不好说）
 * - Markdown 编辑 / 预览双 Tab + 工具栏（R2 语法：![描述|300](url)）
 * - 发帖需算术验证码（PLAN §6.6）
 */
export function EditorModal({ open, board: initialBoard, onClose, onPosted }: EditorModalProps) {
  const { user } = useAuth();
  const [board, setBoard] = useState<'chat' | 'divination' | 'qa'>(initialBoard);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [verdict, setVerdict] = useState('');
  const [tab, setTab] = useState<'edit' | 'preview'>('edit');
  const [captchaSvg, setCaptchaSvg] = useState('');
  const [captchaId, setCaptchaId] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  const loadCaptcha = useCallback(async () => {
    try {
      const res = await getCaptcha();
      setCaptchaId(res.captcha_id);
      setCaptchaSvg(res.svg);
      setCaptchaAnswer('');
    } catch {
      /* 忽略：提交时服务端仍会校验 */
    }
  }, []);

  // 打开时：重置草稿（保留初始板块）+ 拉验证码
  useEffect(() => {
    if (open) {
      setBoard(initialBoard);
      setTitle('');
      setContent('');
      setVerdict('');
      setTab('edit');
      setError('');
      setSubmitting(false);
      void loadCaptcha();
    }
  }, [open, initialBoard, loadCaptcha]);

  if (!open) return null;

  /** 应用工具栏动作：基于光标选区做文本变换 */
  function applyAction(action: (typeof TOOLBAR_ACTIONS)[number]) {
    const el = taRef.current;
    if (!el) return;
    const { text, sel } = action.transform(content, el.selectionStart ?? 0, el.selectionEnd ?? 0);
    setContent(text);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(sel[0], sel[1]);
    });
  }

  async function handleSubmit() {
    if (!user) {
      setError('请先登录');
      return;
    }
    if (!content.trim()) {
      setError('正文不能为空');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const id = await createPost({
        board,
        title: title.trim() || null,
        content,
        verdict: board === 'divination' && verdict ? verdict : null,
        captcha_id: captchaId,
        captcha_answer: Number(captchaAnswer),
      });
      onPosted(id.id);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : '发布失败，请稍后再试');
      void loadCaptcha(); // 验证码已消费，换新
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-wrap">
      <div className="modal-mask" onClick={onClose} />
      <div className="modal-panel">
        <div className="modal-head">
          <p className="modal-title font-zh">发帖</p>
          <button className="icon-btn" onClick={onClose} aria-label="关闭">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        {/* 板块切换 */}
        <div className="seg">
          {(['chat', 'divination', 'qa'] as const).map((b) => (
            <button key={b} className={board === b ? 'on' : ''} onClick={() => setBoard(b)}>
              {BOARD_LABEL[b]}
            </button>
          ))}
        </div>

        {/* 占卜贴灵验标记 */}
        {board === 'divination' && (
          <div className="mt-4">
            <p className="mb-1.5 text-[11px] text-t3">灵验标记（占卜贴可选，默认未定）</p>
            <div className="flex gap-2">
              {['', 'accurate', 'inaccurate', 'uncertain'].map((v) => (
                <button key={v || 'none'} className={`vc${verdict === v ? ' on' : ''}`} onClick={() => setVerdict(v)}>
                  {v ? VERDICT_LABEL[v] : '未定'}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 标题 */}
        <input
          className="field mt-4"
          value={title}
          maxLength={50}
          placeholder={board === 'divination' ? '标题（选填，留空自动生成）' : '标题（2~50 字）'}
          onChange={(e) => setTitle(e.target.value)}
        />

        {/* 编辑 / 预览 Tab + 字数 */}
        <div className="mt-4 flex items-center justify-between">
          <div className="seg" style={{ padding: 3, flex: '0 0 auto' }}>
            <button style={{ minHeight: 28, padding: '0 14px' }} className={tab === 'edit' ? 'on' : ''} onClick={() => setTab('edit')}>
              编辑
            </button>
            <button style={{ minHeight: 28, padding: '0 14px' }} className={tab === 'preview' ? 'on' : ''} onClick={() => setTab('preview')}>
              预览
            </button>
          </div>
          <span className="text-[11px] text-t3">
            {content.length} / {CONTENT_MAX}
          </span>
        </div>

        {tab === 'edit' ? (
          <>
            {/* Markdown 工具栏 */}
            <div className="toolbar mt-2">
              {TOOLBAR_ACTIONS.map((action) => (
                <button
                  key={action.key}
                  type="button"
                  className="tool-btn"
                  title={action.title}
                  aria-label={action.title}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => applyAction(action)}
                >
                  <i className={action.faClass} />
                </button>
              ))}
            </div>
            <textarea
              ref={taRef}
              className="editor-ta mt-2"
              rows={9}
              maxLength={CONTENT_MAX}
              value={content}
              placeholder="正文支持 Markdown：# 标题、**加粗**、> 引用、`代码`、![图片|300](链接)"
              onChange={(e) => setContent(e.target.value)}
            />
          </>
        ) : (
          <div
            className="md-body editor-preview mt-2"
            // 内容经 shared renderMarkdown 转义，安全注入
            dangerouslySetInnerHTML={{
              __html: content.trim() ? renderMarkdown(content) : '<p style="color:var(--t3)">暂无内容</p>',
            }}
          />
        )}

        {/* 算术验证码 */}
        {captchaSvg && (
          <div className="mt-3 flex items-center gap-2">
            <div dangerouslySetInnerHTML={{ __html: captchaSvg }} className="captcha-svg" />
            <input
              className="field"
              inputMode="numeric"
              placeholder="算式答案"
              value={captchaAnswer}
              onChange={(e) => setCaptchaAnswer(e.target.value)}
            />
            <button type="button" className="vc shrink-0" onClick={() => void loadCaptcha()}>
              换一个
            </button>
          </div>
        )}

        {error && <p className="mt-2 text-[11px] text-cinnabar-500">{error}</p>}
        {!user && <p className="mt-2 text-[11px] text-gold-400">登录后才能发帖</p>}

        <div className="modal-foot">
          <button className="ghost-btn" onClick={onClose}>
            取消
          </button>
          <button className="gold-btn" disabled={submitting || !content.trim() || !user} onClick={() => void handleSubmit()}>
            <i className="fa-solid fa-paper-plane" /> {submitting ? '发布中…' : '发布'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** 灵验标记文案 */
const VERDICT_LABEL: Record<string, string> = {
  accurate: '灵',
  inaccurate: '不灵',
  uncertain: '不好说',
};
